import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Images, 
  Search, 
  Filter, 
  Download, 
  Check, 
  X,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Eye,
  Trash2,
  Calendar,
  User,
  Maximize2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import ImageLightbox from '../../components/ImageLightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Memoized Thumbnail-Komponente für bessere Performance
const ImageThumbnail = memo(({ imageId, token, alt }) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const thumbnailUrl = `${API}/images/${imageId}/thumbnail?t=${token}`;
  
  if (error) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
        <Images className="w-8 h-8 text-slate-400" />
      </div>
    );
  }
  
  return (
    <>
      {loading && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img
        src={thumbnailUrl}
        alt={alt || 'Bild'}
        className={`w-full h-full object-cover transition-opacity duration-200 ${loading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setError(true);
          setLoading(false);
        }}
      />
    </>
  );
});

ImageThumbnail.displayName = 'ImageThumbnail';

const MediaLibraryPage = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [month, setMonth] = useState(searchParams.get('month') || 'all');
  const [year, setYear] = useState(searchParams.get('year') || 'all');
  const [usageStatus, setUsageStatus] = useState(searchParams.get('usage') || 'all');
  const [guestEmail, setGuestEmail] = useState(searchParams.get('guest') || 'all');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const navigateLightbox = useCallback((newIndex) => {
    setLightboxIndex(newIndex);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 24);
      if (search) params.set('search', search);
      if (category && category !== 'all') params.set('category', category);
      if (month && month !== 'all') params.set('month', month);
      if (year && year !== 'all') params.set('year', year);
      if (usageStatus && usageStatus !== 'all') params.set('usage_status', usageStatus);
      if (guestEmail && guestEmail !== 'all') params.set('sender_email', guestEmail);

      const [imagesRes, categoriesRes, guestsRes] = await Promise.all([
        axios.get(`${API}/images?${params.toString()}`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/dashboard/guests`)
      ]);

      setImages(imagesRes.data.images);
      setTotalPages(imagesRes.data.pages);
      setTotalImages(imagesRes.data.total);
      setCategories(categoriesRes.data);
      setGuests(guestsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [page, search, category, month, year, usageStatus, guestEmail]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (search) params.set('search', search);
    if (category && category !== 'all') params.set('category', category);
    if (month && month !== 'all') params.set('month', month);
    if (year && year !== 'all') params.set('year', year);
    if (usageStatus && usageStatus !== 'all') params.set('usage', usageStatus);
    if (guestEmail && guestEmail !== 'all') params.set('guest', guestEmail);
    setSearchParams(params);
  }, [page, search, category, month, year, usageStatus, guestEmail, setSearchParams]);

  const handleSelectAll = () => {
    if (selectedImages.length === images.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(images.map(img => img.id));
    }
  };

  const handleSelectImage = (imageId) => {
    if (selectedImages.includes(imageId)) {
      setSelectedImages(prev => prev.filter(id => id !== imageId));
    } else {
      setSelectedImages(prev => [...prev, imageId]);
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedImages.length === 0) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (selectedImages.length === 1) {
        // Single download
        const response = await axios.get(`${API}/images/${selectedImages[0]}/download`, {
          responseType: 'blob',
          headers
        });
        const image = images.find(img => img.id === selectedImages[0]);
        const filename = image?.original_filename || 'bild.jpg';
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Multiple download as ZIP
        toast.info(`ZIP-Datei wird erstellt mit ${selectedImages.length} Bildern...`);
        const response = await axios.post(`${API}/images/download-multiple`, selectedImages, {
          responseType: 'blob',
          headers
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'gb-sylt-bilder.zip';
        link.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success('Download gestartet');
      setSelectedImages([]);
      fetchData(); // Refresh to update download counts
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download fehlgeschlagen');
    }
  };

  const handleSingleDownload = async (imageId) => {
    try {
      const response = await axios.get(`${API}/images/${imageId}/download`, {
        responseType: 'blob'
      });
      const image = images.find(img => img.id === imageId);
      const filename = image?.original_filename || 'bild.jpg';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download gestartet');
      fetchData(); // Refresh to update download counts
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download fehlgeschlagen');
    }
  };

  const resetFilters = () => {
    setSearch('');
    setCategory('all');
    setMonth('all');
    setYear('all');
    setUsageStatus('all');
    setGuestEmail('all');
    setPage(1);
  };

  const getDownloadBadge = (image) => {
    if (image.download_count === 0) return { label: 'Neu', color: 'bg-green-100 text-green-800' };
    if (image.download_count >= 3) return { label: 'Mehrfach verwendet', color: 'bg-purple-100 text-purple-800' };
    return { label: 'Heruntergeladen', color: 'bg-blue-100 text-blue-800' };
  };

  const months = [
    { value: '1', label: 'Januar' },
    { value: '2', label: 'Februar' },
    { value: '3', label: 'März' },
    { value: '4', label: 'April' },
    { value: '5', label: 'Mai' },
    { value: '6', label: 'Juni' },
    { value: '7', label: 'Juli' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Dezember' },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

  const FilterSidebar = () => (
    <div className="space-y-6">
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Kategorie</label>
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-category">
            <SelectValue placeholder="Alle Kategorien" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kategorien</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Monat</label>
        <Select value={month} onValueChange={(v) => { setMonth(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-month">
            <SelectValue placeholder="Alle Monate" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Monate</SelectItem>
            {months.map(m => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Jahr</label>
        <Select value={year} onValueChange={(v) => { setYear(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-year">
            <SelectValue placeholder="Alle Jahre" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Jahre</SelectItem>
            {years.map(y => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Verwendungsstatus</label>
        <Select value={usageStatus} onValueChange={(v) => { setUsageStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-usage">
            <SelectValue placeholder="Alle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="verwendet">Verwendet</SelectItem>
            <SelectItem value="nicht_verwendet">Nicht verwendet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Gast</label>
        <Select value={guestEmail} onValueChange={(v) => { setGuestEmail(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-guest">
            <SelectValue placeholder="Alle Gäste" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Gäste</SelectItem>
            {guests.map(guest => (
              <SelectItem key={guest.email} value={guest.email}>{guest.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button variant="outline" className="w-full" onClick={resetFilters} data-testid="reset-filters">
        Filter zurücksetzen
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="media-library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 heading-app">Medienbibliothek</h1>
          <p className="text-slate-500">{totalImages} Bilder insgesamt</p>
        </div>
        
        {selectedImages.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">{selectedImages.length} ausgewählt</span>
            <Button onClick={handleDownloadSelected} data-testid="download-selected">
              <Download className="w-4 h-4 mr-2" />
              Herunterladen
            </Button>
            <Button variant="outline" onClick={() => setSelectedImages([])} data-testid="clear-selection">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Suchen nach Name, Beschreibung..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <div className="flex gap-2">
          {/* Mobile Filter */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-button">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <SheetHeader>
                <SheetTitle>Filter</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterSidebar />
              </div>
            </SheetContent>
          </Sheet>

          <Button variant="outline" onClick={handleSelectAll} data-testid="select-all">
            <Check className="w-4 h-4 mr-2" />
            {selectedImages.length === images.length ? 'Auswahl aufheben' : 'Alle auswählen'}
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-medium text-slate-900 mb-4">Filter</h3>
              <FilterSidebar />
            </CardContent>
          </Card>
        </aside>

        {/* Image Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-16">
              <Images className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Bilder gefunden</h3>
              <p className="text-slate-500">Passen Sie Ihre Filter an oder warten Sie auf neue Uploads.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image, index) => {
                  const badge = getDownloadBadge(image);
                  const isSelected = selectedImages.includes(image.id);
                  
                  return (
                    <div 
                      key={image.id}
                      className={`relative group rounded-xl overflow-hidden bg-slate-100 aspect-square cursor-pointer ${isSelected ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => openLightbox(index)}
                    >
                      {/* Echtes Thumbnail-Bild */}
                      <ImageThumbnail 
                        imageId={image.id} 
                        token={token} 
                        alt={image.original_filename}
                      />

                      {/* Selection Checkbox */}
                      <div 
                        className="absolute top-2 left-2 z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleSelectImage(image.id)}
                          className="bg-white"
                          data-testid={`select-image-${image.id}`}
                        />
                      </div>

                      {/* Badge */}
                      <div className="absolute top-2 right-2 z-10">
                        <Badge className={badge.color}>{badge.label}</Badge>
                      </div>

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLightbox(index);
                          }}
                          data-testid={`preview-image-${image.id}`}
                        >
                          <Maximize2 className="w-4 h-4" />
                        </Button>
                        <Link 
                          to={`/admin/media/${image.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button size="sm" variant="secondary" data-testid={`view-image-${image.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="secondary">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/media/${image.id}`}>
                                <Eye className="w-4 h-4 mr-2" />
                                Details anzeigen
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSingleDownload(image.id)}>
                              <Download className="w-4 h-4 mr-2" />
                              Herunterladen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Info */}
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                        <p className="text-white text-xs truncate font-medium">{image.sender_name}</p>
                        <p className="text-white/70 text-xs">{image.upload_date}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Seite {page} von {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                    data-testid="next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        onNavigate={navigateLightbox}
        token={token}
        onDownload={handleSingleDownload}
      />
    </div>
  );
};

export default MediaLibraryPage;
